/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { Button, type ContextValue, DEFAULT_SLOT, Provider, RadioGroup, RadioGroupContext, TreeItemContent as TreeItemContentCore, type TreeItemContentProps as TreeItemContentCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { Checkbox, CheckboxContext } from '../checkbox/checkbox.component';
import { Radio, RadioContext } from '../radio/radio.component';
import { Text, TextContext } from '../text/text.component';

interface TreeItemContentProps extends Omit<TreeItemContentCoreProps, 'style' | 'className'> {
  style?: stylex.StyleXStyles;
}

const TreeItemContentContext = createContext<ContextValue<TreeItemContentProps, HTMLDivElement>>({});

const TreeItemContent = forwardRef((props: TreeItemContentProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TreeItemContentContext);

  const { style, children, ...coreProps } = props;

  return (
    <TreeItemContentCore ref={ref} {...stylex.props(styles.base, style)} {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [CheckboxContext, {
              slots: {
                selection: {
                  size: 20,
                  style: checkboxStyles.base,
                }
              }
            }],
            [RadioContext, {
              slots: {
                selection: {
                  size: 20,
                  value: renderProps.id.toString(),
                }
              }
            }],
            [RadioGroupContext, stylex.props(checkboxStyles.base)],
            [TextContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 16,
                  color: renderProps.isSelected || renderProps.isHovered ? 'primary' : 'secondary',
                  weight: renderProps.isSelected ? 'medium' : 'regular',
                  style: textStyles.base,
                },
                [TREE_ITEM_CONTENT_SLOTS.CHILDREN_COUNT]: {
                  size: 16,
                  color: 'placeholder',
                }
              }
            }]
          ]}>
            <Button
              slot="chevron"
              {...stylex.props(
                buttonStyles.base,
                renderProps.hasChildItems && buttonStyles.visible,
                renderProps.isExpanded && buttonStyles.chevronOpen
              )}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M8.96667 5.59995C9.23333 5.79995 9.23333 6.19995 8.96667 6.39995L4.1 10.0499C3.85279 10.2354 3.5 10.059 3.5 9.74995L3.5 2.24995C3.5 1.94093 3.85279 1.76454 4.1 1.94995L8.96667 5.59995Z" fill="#B3B3B3" />
              </svg>
            </Button>

            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}

            {renderProps.selectionBehavior === 'toggle' && renderProps.selectionMode === 'multiple' && (
              <Checkbox
                slot="selection"
                isIndeterminate={renderProps.hasChildItems}
                style={{
                  base: checkboxStyles.base,
                  label: [checkboxStyles.label, renderProps.isHovered && checkboxStyles.labelHovered],
                  icon: (checkboxRenderProps) => renderProps.isHovered && !checkboxRenderProps.isSelected && checkboxStyles.iconHovered
                }}>
                {() => renderProps.hasChildItems && renderProps.isHovered ? renderProps.isSelected ? 'Unselect all' : 'Select all' : null}
              </Checkbox>)}

            {renderProps.selectionBehavior === 'toggle' && renderProps.selectionMode === 'single' && (
              <RadioGroup
                value={renderProps.isSelected ? renderProps.id.toString() : null}
                aria-labelledby={renderProps.id.toString()}
                aria-hidden>
                <Radio
                  slot="selection"
                  onClick={() => renderProps.state.selectionManager.toggleSelection(renderProps.id)}
                  value={renderProps.id.toString()}
                  style={{
                    base: (radioRenderProps) => [radioStyles.base, renderProps.isHovered && !radioRenderProps.isSelected && radioStyles.hovered],
                    label: [radioStyles.label, renderProps.isHovered && radioStyles.labelHovered],
                  }}>
                  {() => renderProps.hasChildItems && renderProps.isHovered ? renderProps.isSelected ? 'Unselect all' : 'Select all' : null}
                </Radio>
              </RadioGroup>)}
          </Provider>
        );
      }}
    </TreeItemContentCore>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    display: 'flex',
    gap: 12,
    paddingBlock: 10,
    paddingInline: 8,
  },
});

const textStyles = stylex.create({
  base: {
    marginRight: 'auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
});

const buttonStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderStyle: 'none',
    cursor: 'inherit',
    display: 'flex',
    margin: 0,
    marginLeft: 'calc((var(--tree-item-level) - 1) * 18px)',
    minHeight: 14,
    minWidth: 24,
    transitionDuration: '200ms',
    transitionProperty: 'transform',
    transitionTimingFunction: 'ease-in-out',
    visibility: 'hidden',
  },
  visible: {
    marginLeft: 'calc((var(--tree-item-level) - 1) * 25px)',
    visibility: 'visible',
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
  },
});


const checkboxStyles = stylex.create({
  base: {
    flexDirection: 'row-reverse',
  },
  label: {
    color: $.textPlaceholder,
    fontSize: 13,
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: -0.26,
    lineHeight: '16px',
    width: 'max-content',
  },
  labelHovered: {
    color: $.textInteractive,
  },
  iconHovered: {
    borderColor: $.borderContrast,
  }
});

const radioStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  label: {
    color: $.textPlaceholder,
    fontSize: 13,
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: -0.26,
    lineHeight: '16px',
  },
  labelHovered: {
    color: $.textInteractive,
  },
  hovered: {
    '::before': {
      borderColor: $.borderContrast,
      borderWidth: 1.5,
    }
  }
});

const TREE_ITEM_CONTENT_SLOTS = {
  CHILDREN_COUNT: 'childrenCount'
};

export { TREE_ITEM_CONTENT_SLOTS, TreeItemContent, TreeItemContentContext, type TreeItemContentProps };
